import { berlinZeitpunkt } from "@dj/domain/gemeinsam";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { abweisungsgrund, befehl } from "../plattform/api";
import { Knopf } from "../design/Kachel";
import { Abweisung, Meldung, Status } from "../design/Hinweis";
import { Textfeld } from "../design/Formular";
import { geraetebezeichnungVorschlag } from "../plattform/geraet";
import { useDokument } from "../plattform/lesemodelle";
import { abmelden, useSitzung } from "../plattform/sitzung";
import { VERSION } from "../plattform/version";
import { KontaktDialog } from "./KontaktDialog";
import { usePasskeyzeremonie } from "./passkey";
import { passkeymeldung } from "./zugangsmeldungen";

/**
 * Kontoansicht (oberflaeche.md §7, lesemodelle.md §6): ohne Ansprache und Anredepronomen (§2.3). Nach einem Umzugslink
 * (Umzug.tsx) ist der Dialog „Passkey“ gleich geöffnet; nach dem Erstellen geht es zu Meine Drückjagden.
 */
export function Kontoansicht() {
  const { uid, art } = useSitzung();
  const navigate = useNavigate();
  const ausUmzug = (useLocation().state as { umzug?: boolean } | null)?.umzug === true;
  const konto = useDokument(uid && art === "passkey" ? `personen/${uid}/konto/aktuell` : null);
  const [kontaktOffen, setKontaktOffen] = useState(false);
  const passkeys = (konto.daten?.["Passkeys"] as Array<Record<string, unknown>> | undefined) ?? [];
  const kontakte = (konto.daten?.["Wiederherstellungskontakte"] as Array<Record<string, unknown>> | undefined) ?? [];
  const kennungen = (konto.daten?.["_passkeys"] as string[] | undefined) ?? [];
  const [grund, setGrund] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const zeremonie = usePasskeyzeremonie("registrierung", true);
  const [loeschen, setLoeschen] = useState(false);
  const [hinzufuegen, setHinzufuegen] = useState(ausUmzug);
  const [geraet, setGeraet] = useState(geraetebezeichnungVorschlag);
  const [laeuft, setLaeuft] = useState(false);
  async function passkeyEntfernen(i: number) {
    setGrund(null);
    const a = await befehl("PasskeyEntfernen", { Passkey: kennungen[i] });
    if (!a.ok) setGrund(abweisungsgrund(a));
  }
  /**
   * Weiterer Passkey (F-04B, identitaeten.md §3.1 `zweck` `hinzufuegen`): die Zeremonie startet mit „Speichern“, im
   * Handler des Fingertipps (Passkeyzeremonie). Jeder Fehlschlag hat seine Meldung (oberflaeche.md §9, V-20).
   */
  function passkeySpeichern() {
    setGrund(null);
    setMeldung(null);
    setLaeuft(true);
    void zeremonie.ausfuehren({ zweck: "hinzufuegen", "Gerätebezeichnung": geraet }).then((r) => {
      setLaeuft(false);
      setHinzufuegen(false);
      if (!r.fehler && ausUmzug) {
        navigate("/", { replace: true });
        return;
      }
      if (r.fehler === "abgelehnt") setGrund(r.antwort.grund ?? "unzulaessige-angaben");
      else setMeldung(passkeymeldung("konto", r.fehler));
    });
  }
  async function kontaktEntfernen(adresse: string) {
    setGrund(null);
    const a = await befehl("WiederherstellungskontaktEntfernen", { Adresse: adresse });
    if (!a.ok) setGrund(abweisungsgrund(a));
  }
  async function kontoLoeschen() {
    setLoeschen(false);
    setGrund(null);
    const a = await befehl("KontoLoeschen", {});
    if (!a.ok) { setGrund(abweisungsgrund(a)); return; }
    await abmelden();
    navigate("/", { replace: true });
  }
  return (
    <Ansicht name="Kontoansicht" titel="Konto" symbol="passkey">
      {art === "passkey" ? (
        <>
          <div className="liste-bereich platte">
            <h2 className="kopf">Passkeys</h2>
            <table className="tabelle">
              <thead><tr><th>Gerät</th><th>erstellt am</th><th>zuletzt verwendet am</th><th aria-label="Aktion" /></tr></thead>
              <tbody>
                {passkeys.map((p, i) => (
                  <tr key={i}>
                    <td>{String(p["Gerät"])}</td>
                    <td>{p["erstellt am"] instanceof Date ? berlinZeitpunkt(p["erstellt am"]) : "—"}</td>
                    <td>{p["zuletzt verwendet am"] instanceof Date ? berlinZeitpunkt(p["zuletzt verwendet am"]) : "—"}</td>
                    <td>{kennungen[i] ? <Knopf art="planung-zweit" onPress={() => passkeyEntfernen(i)}>Entfernen</Knopf> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Knopf art="planung" symbol="passkey" onPress={() => setHinzufuegen(true)}>Hinzufügen</Knopf>
          </div>
          <section className="text-bereich platte" role="region" aria-label="Wiederherstellungskontakte">
            <h2 className="kopf">Wiederherstellungskontakte</h2>
            {kontakte.map((k, i) => (
              <div key={i} className="zeile">
                <div className="zeile__t">{String(k["Adresse"])}</div>
                <Knopf art="planung-zweit" onPress={() => kontaktEntfernen(String(k["Adresse"]))}>Entfernen</Knopf>
              </div>
            ))}
            {konto.daten && kontakte.length === 0 ? <Status>Kein Wiederherstellungskontakt hinterlegt</Status> : null}
            <Knopf art="planung" symbol="telefon" onPress={() => setKontaktOffen(true)}>Hinterlegen</Knopf>
          </section>
        </>
      ) : null}
      <Abweisung grund={grund} />
      {meldung ? <Meldung>{meldung}</Meldung> : null}
      <div className="knopfreihe">
        {art === "passkey" ? <Knopf art="planung" symbol="hund" onPress={() => navigate("/konto/hunde")}>Hunde</Knopf> : null}
        <Knopf art="zweit" symbol="zurueck" onPress={async () => { await abmelden(); navigate("/", { replace: true }); }}>Abmelden</Knopf>
        {art === "passkey" ? <Knopf art="zweit" symbol="schliessen" onPress={() => setLoeschen(true)}>Löschen</Knopf> : null}
      </div>
      <p className="kontoansicht__version">Version {VERSION}</p>
      <Dialog name="Löschen" offen={loeschen} bestaetigung onSchliessen={() => setLoeschen(false)}>
        <p>Konto endgültig löschen? Alle Verknüpfungen mit Revieren enden; Meldungen in den Revieren bleiben erhalten.</p>
        <div className="knopfreihe">
          <Knopf onPress={kontoLoeschen}>Bestätigen</Knopf>
          <Knopf art="zweit" onPress={() => setLoeschen(false)}>Abbrechen</Knopf>
        </div>
      </Dialog>
      <Dialog name="Passkey" symbol="passkey" offen={hinzufuegen} onSchliessen={() => setHinzufuegen(false)}>
        <div className="formular">
          <Textfeld label="Gerätebezeichnung" wert={geraet} onChange={setGeraet} />
          <div className="knopfreihe">
            <Knopf onPress={passkeySpeichern} isDisabled={laeuft}>Speichern</Knopf>
            <Knopf art="zweit" onPress={() => setHinzufuegen(false)}>Abbrechen</Knopf>
          </div>
        </div>
      </Dialog>
      <KontaktDialog offen={kontaktOffen} onSchliessen={() => setKontaktOffen(false)} />
    </Ansicht>
  );
}
